import { FC, useRef, useEffect } from 'react';
import { Modal, ButtonProps } from '@cfa/react-core';
import { LoadingButton } from '../../Styled/LoadingButton';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../../styles/globalStyleVars';
import { IconWithShadow } from '../../Styled/IconWithShadow';
import useBreakpoints from '../../../hooks/useBreakpoints';
import useMediaQuery from '../../../hooks/useMediaQuery';

const SIDE_PADDING_DESKTOP = '64px';
const SCROLLBAR_GUTTER = '28px';

/**
 * The effective content inset the notification band bleeds by, in px - a bare
 * number would make the band's negative margin invalid CSS.
 */
export const resolveMessageBleed = (
  override: React.CSSProperties['paddingInline'],
  fallback: string
): string => {
  const value = override ?? fallback;
  return typeof value === 'number' ? `${value}px` : value;
};

const FooterButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${themeSizes.sm};
`;

interface SharedModalProps {
  bodyStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  footerStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  button?: {
    disabled?: {
      primary?: boolean;
      secondary?: boolean;
    };
    loading?: {
      primary?: boolean;
      secondary?: boolean;
    };
    submitting?: {
      primary?: boolean;
      secondary?: boolean;
    };
    onClick?: {
      primary?: () => void;
      secondary?: () => void;
    };
    icon?: {
      primary?: React.ReactNode;
      secondary?: React.ReactNode;
    };
    text?: { primary?: string | null; secondary?: string | null };
    variant?: {
      secondary: ButtonProps['variant'];
      primary: ButtonProps['variant'];
    };
    minWidth?: {
      primary?: React.CSSProperties['minWidth'];
      secondary?: React.CSSProperties['minWidth'];
    };
    style?: {
      primary?: React.CSSProperties;
      secondary?: React.CSSProperties;
    };
  };
  suppressClickOutside?: boolean;
  dataTestId?: string;
  error?: {
    hasError: boolean | undefined;
    message: React.JSX.Element;
  };
  headerIcon?: React.JSX.Element;
  headerIconStyle?: React.CSSProperties;
  headerIconWithShadow?: boolean;
  onClose: () => void;
  onExited?: () => void;
  show: boolean;
  showCloseButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  success?: {
    isSuccess: boolean | undefined;
    message: React.JSX.Element;
  };
  text: {
    body?: string | React.JSX.Element | React.JSX.Element[];
    footer?: React.JSX.Element;
    header: string | React.JSX.Element | null;
    message?: React.JSX.Element;
  };
  showFooter?: boolean;
}

interface FooterMessageProps {
  $sidePadding: string;
}

const BodyWrapper = styled.div<{
  $isSmAndDown: boolean;
}>`
  width: 100%;
  box-sizing: border-box;
  max-height: calc(90vh - 200px);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: ${themeColors.colorGray_3} transparent;
  color: ${themeColors.colorGray_7};

  /* Stretches into the content's right padding on desktop so the scrollbar sits
     in the gutter, not against the text - the offset and padding have to match. */
  ${({ $isSmAndDown }) =>
    !$isSmAndDown &&
    `
      width: calc(100% + ${SCROLLBAR_GUTTER});
      margin-right: -${SCROLLBAR_GUTTER};
      padding-right: ${SCROLLBAR_GUTTER};
    `}
`;

const StyledFooter = styled(Modal.Footer)<{
  $stackButtons: boolean;
}>`
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap-reverse;
  gap: ${themeSizes.sm};

  /* Stacks the two buttons below sm, reversed because the secondary is rendered
     first for tab order and the primary belongs on top. */
  ${({ $stackButtons }) =>
    $stackButtons &&
    `
      flex-direction: column-reverse;
      flex-wrap: nowrap;
    `}
  padding: ${themeSizes.smMd} 0 ${themeSizes.xl} 0;
`;

const HeaderIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: -${themeSizes.sm};
`;

/**
 * Bleeds past the content's side padding so the band reaches the dialog edges,
 * tracking the effective inset so a `contentStyle` override still lines up.
 */
const FooterMessage = styled.div<FooterMessageProps>`
  width: calc(100% + (${({ $sidePadding }) => $sidePadding} * 2));
  margin-inline: -${({ $sidePadding }) => $sidePadding};
  box-sizing: border-box;
  min-height: 67px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${themeColors.notificationBackgroundColor};

  /* Only the background bleeds - the text takes the content box's inset back so
     it lines up with the modal's body copy. */
  & > * {
    width: 100%;
    padding-inline: ${({ $sidePadding }) => $sidePadding};
    box-sizing: border-box;
  }
`;

const FooterContent = styled.div`
  display: contents;
`;

const SharedModal: FC<SharedModalProps> = ({
  bodyStyle,
  contentStyle,
  footerStyle,
  headerStyle,
  button,
  suppressClickOutside,
  dataTestId,
  error,
  headerIcon,
  headerIconStyle,
  headerIconWithShadow = false,
  onClose,
  onExited,
  show,
  showCloseButton = true,
  size = 'md',
  success,
  text,
  showFooter = true,
}) => {
  const {
    text: buttonText = {},
    onClick = {},
    icon: buttonIcon = {},
    disabled = {},
    loading = {},
    //submitting = {}, // commenting out submitting prop for now in SAW-9273
    minWidth: buttonWidth = {},
    style: buttonStyle = {},
  } = button || {};
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));
  const sidePadding = isSmAndDown ? themeSizes.lg : SIDE_PADDING_DESKTOP;

  const messageBleed = resolveMessageBleed(
    contentStyle?.paddingInline,
    sidePadding
  );
  const stackButtons =
    isSmAndDown && Boolean(buttonText?.secondary && buttonText?.primary);
  const stackedButtonStyle: React.CSSProperties | undefined = stackButtons
    ? { width: '100%' }
    : undefined;

  const prevShowRef = useRef(show);
  useEffect(() => {
    if (prevShowRef.current && !show) {
      onExited?.();
    }
    prevShowRef.current = show;
  }, [show, onExited]);

  return (
    <Modal.Root>
      <Modal.Overlay
        isOpen={show}
        onOpenChange={isOpen => {
          if (!isOpen) onClose();
        }}
        isDismissable={!suppressClickOutside}
        style={{ position: 'fixed', zIndex: 9999 }}
      >
        <Modal.Dialog
          data-testid={dataTestId}
          size={size}
          isCloseButtonHidden={!showCloseButton}
          style={{ maxHeight: '90vh', overflow: 'hidden', zIndex: 10000 }}
        >
          <Modal.Content
            style={{ paddingInline: sidePadding, ...contentStyle }}
          >
            {error?.hasError && error.message}
            {success?.isSuccess && success.message}
            {headerIcon && (
              <HeaderIconWrapper style={headerIconStyle}>
                {headerIconWithShadow ? (
                  <IconWithShadow tablerIcon={headerIcon} />
                ) : (
                  headerIcon
                )}
              </HeaderIconWrapper>
            )}
            {text.header && (
              <Modal.Title
                level={3}
                style={{ width: '100%', padding: 0, ...headerStyle }}
              >
                {text.header as React.ReactNode}
              </Modal.Title>
            )}
            <BodyWrapper $isSmAndDown={isSmAndDown} style={bodyStyle}>
              {text.body as React.ReactNode}
            </BodyWrapper>
            {showFooter && (
              <StyledFooter
                $stackButtons={stackButtons}
                style={
                  !buttonText?.secondary
                    ? { ...footerStyle, justifyContent: 'center' }
                    : footerStyle
                }
              >
                <FooterContent data-testid='modal-footer'>
                  {text.footer}
                  {buttonText?.secondary && (
                    <LoadingButton
                      isDisabled={disabled?.secondary}
                      isPending={loading?.secondary}
                      onPress={onClick.secondary}
                      size='md'
                      style={{
                        ...stackedButtonStyle,
                        ...(buttonWidth.secondary != null
                          ? { minWidth: buttonWidth.secondary }
                          : {}),
                        ...buttonStyle.secondary,
                      }}
                      variant={
                        button?.variant?.secondary
                          ? button.variant.secondary
                          : 'outlined'
                      }
                    >
                      <FooterButtonContent>
                        {buttonText.secondary}
                        {buttonIcon.secondary}
                      </FooterButtonContent>
                    </LoadingButton>
                  )}
                  {buttonText?.primary && (
                    <LoadingButton
                      isDisabled={disabled?.primary}
                      isPending={loading?.primary}
                      onPress={onClick.primary}
                      size='md'
                      style={{
                        ...stackedButtonStyle,
                        ...(buttonWidth.primary != null
                          ? { minWidth: buttonWidth.primary }
                          : {}),
                        ...buttonStyle.primary,
                      }}
                      variant={
                        button?.variant?.primary
                          ? button.variant.primary
                          : 'filled'
                      }
                    >
                      <FooterButtonContent>
                        {buttonText.primary}
                        {buttonIcon.primary}
                      </FooterButtonContent>
                    </LoadingButton>
                  )}
                </FooterContent>
              </StyledFooter>
            )}
            {text.message && (
              <FooterMessage $sidePadding={messageBleed}>
                {text.message as React.ReactNode}
              </FooterMessage>
            )}
          </Modal.Content>
        </Modal.Dialog>
      </Modal.Overlay>
    </Modal.Root>
  );
};

export default SharedModal;
